import { useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CanceledError } from "axios";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { Car, CarSearch } from "@models/car";
import { buildCarSort, CarSearchParams, searchCars } from "../../../api/cars.service";
import { useEntityBrowse } from "../../../hooks/useEntityBrowse";
import { DEFAULT_SORT_KEY, parseCarsUrlParams, syncCarsUrlParams } from "./carsUrlParams";

export function useCarsBrowse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseCarsUrlParams(searchParams), [searchParams]);

  const [data, setData] = useState<Car[]>([]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const carSearchForm = useForm<CarSearch>({
    defaultValues: urlParams.filters ?? {},
  });

  async function getData(searchData: CarSearch): Promise<void> {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const sort = buildCarSort(sortKey, sortMethod);
      const payload: CarSearchParams = {
        filter: deleteEmptyFieldsFromObject(searchData),
        sort: [sort],
      };
      const cars = await searchCars(payload, abortControllerRef.current.signal);
      setData(cars.data);
      setIsLoading(false);
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        return;
      }
      handleErrorWithBoundary(error, showBoundary);
      setIsLoading(false);
    }
  }

  const onSearch: SubmitHandler<CarSearch> = (formData) => {
    syncCarsUrlParams(setSearchParams, formData, sortKey, sortMethod);
    getData(formData);
  };

  useEntityBrowse({
    form: carSearchForm,
    sortKey,
    sortMethod,
    onSearch,
  });

  function handleSortKeyChanged(value: string) {
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function reset() {
    const values = setAllValuesToNull(carSearchForm.getValues());

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        carSearchForm.setValue(key as keyof CarSearch, values[key as keyof CarSearch]);
      }
    }
  }

  return {
    carSearchForm,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    reset,
  };
}
